/* ADDAX cart synchronization v1. Installed at the start of assets/cart.js. */
(() => {
  if (window.AddaxCart) return;
  let tail = Promise.resolve();
  let pending = 0;
  let revision = 0;
  let refreshPromise;
  let uncertain = false;
  let noteDraft;
  let noteChangedAt = 0;
  let notePromise;
  let focusBefore;
  const disabled = new Map();
  const inertContainers = new Map();
  const controls = 'cart-items quantity-input button, cart-items quantity-input input, cart-drawer quantity-input button, cart-drawer quantity-input input, cart-remove-button button, addax-cart-suggestions .acs__add, addax-cart-suggestions select, button[name="checkout"], input[name="checkout"]';
  const cartUrl = () => window.routes?.cart_url || '/cart';
  const endpoint = name => window.routes?.[`cart_${name}_url`] || `${cartUrl()}/${name}.js`;
  const messageFor = result => typeof result?.description === 'string' ? result.description : typeof result?.errors === 'string' ? result.errors : 'Your bag could not be updated. Please refresh your bag.';

  function lock() {
    if (!pending && !uncertain) return;
    document.querySelectorAll(controls).forEach(field => {
      if (!disabled.has(field)) disabled.set(field, field.disabled);
      field.disabled = true;
    });
    document.querySelectorAll('cart-drawer, cart-items').forEach(el => el.setAttribute('aria-busy', 'true'));
    document.querySelectorAll('cart-remove-button a').forEach(el => el.setAttribute('aria-disabled', 'true'));
    document.querySelectorAll('.cart__dynamic-checkout-buttons, .additional-checkout-buttons, shopify-accelerated-checkout-cart').forEach(el => {
      if (!inertContainers.has(el)) inertContainers.set(el, el.inert);
      el.inert = true;
    });
  }
  function unlock() {
    if (pending || uncertain) { lock(); return; }
    for (const [field, original] of disabled) if (field.isConnected) field.disabled = original;
    disabled.clear();
    for (const [el, original] of inertContainers) if (el.isConnected) el.inert = original;
    inertContainers.clear();
    document.querySelectorAll('cart-drawer, cart-items').forEach(el => el.removeAttribute('aria-busy'));
    document.querySelectorAll('cart-remove-button a').forEach(el => el.removeAttribute('aria-disabled'));
    const drawer = document.querySelector('cart-drawer.active');
    const root = drawer || (focusBefore?.inMain ? document.querySelector('cart-items') : null);
    if (root) {
      const input = [...root.querySelectorAll('input[data-cart-line-key]')].find(el => el.dataset.cartLineKey === focusBefore?.key);
      const row = input?.closest('.cart-item');
      const candidate = focusBefore?.name === 'updates[]' ? input : [...(row?.querySelectorAll('button') || [])].find(el => el.name === focusBefore?.name);
      const current = root.contains(document.activeElement) && !document.activeElement.disabled ? document.activeElement : null;
      const focus = candidate || current || root.querySelector('.drawer__close, .cart-item__name, a');
      // Refresh the focus trap after controls are enabled, including the checkout button.
      if (drawer && typeof trapFocus === 'function') trapFocus(drawer.querySelector('#CartDrawer'), focus);
      else focus?.focus({ preventScroll: true });
    }
    focusBefore = undefined;
  }
  function report(message, reload = false) {
    const drawer = document.querySelector('cart-drawer');
    const host = drawer?.classList.contains('active') ? drawer.querySelector('.drawer__inner') : document.querySelector('#main-cart-footer') || drawer?.querySelector('.drawer__inner');
    if (!host) return;
    host.querySelector('[data-addax-cart-message]')?.remove();
    const notice = document.createElement('p');
    notice.dataset.addaxCartMessage = '';
    notice.setAttribute('role', 'alert');
    notice.textContent = message;
    if (host.closest('cart-drawer')) notice.className = 'acs__recovery-message';
    if (reload) {
      notice.append(document.createTextNode(' '));
      const link = document.createElement('a');
      link.href = cartUrl();
      link.textContent = 'Refresh your bag';
      link.style.textDecoration = 'underline';
      notice.append(link);
    } else setTimeout(() => notice.remove(), 8000);
    host.append(notice);
  }
  function run(work, invalidatesRead = true) {
    if (uncertain) return Promise.reject(new Error('Please refresh your bag before making another change.'));
    const ticket = invalidatesRead ? ++revision : revision;
    if (!pending) {
      const active = document.activeElement;
      focusBefore = { key: active?.closest('.cart-item')?.querySelector('[data-cart-line-key]')?.dataset.cartLineKey, name: active?.getAttribute('name'), inMain: !!active?.closest('cart-items') };
    }
    pending++;
    lock(); // Reserve immediately, before a network request or debounce.
    const job = tail.then(() => {
      if (uncertain) throw new Error('Please refresh your bag before making another change.');
      return work(ticket);
    });
    tail = job.catch(() => {});
    return job.finally(() => { pending--; unlock(); });
  }
  function plans() {
    const list = [];
    if (document.querySelector('#CartDrawer')) list.push({ section: 'cart-drawer', target: '#CartDrawer', source: '#CartDrawer' });
    if (document.querySelector('#cart-icon-bubble')) list.push({ section: 'cart-icon-bubble', target: '#cart-icon-bubble', source: '.shopify-section' });
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (items) list.push({ section: items.dataset.id, target: 'cart-items', source: 'cart-items' });
    if (footer) list.push({ section: footer.dataset.id, target: '#main-cart-footer', source: '#main-cart-footer' });
    return list.filter(plan => plan.section);
  }
  function fragments(sections, list) {
    return list.map(plan => {
      if (typeof sections?.[plan.section] !== 'string') throw new Error('Cart section missing');
      const html = new DOMParser().parseFromString(sections[plan.section], 'text/html');
      const source = html.querySelector(plan.source);
      if (!source) throw new Error('Cart section incomplete');
      return { ...plan, source, html };
    });
  }
  function render(prepared, options = {}) {
    const drawer = document.querySelector('cart-drawer');
    const wasOpen = drawer?.classList.contains('active');
    const active = document.activeElement;
    const activeKey = active?.closest('.cart-item')?.querySelector('[data-cart-line-key]')?.dataset.cartLineKey;
    const activeName = active?.getAttribute('name');
    const scroll = ['cart-drawer-items', '.addax-cart-body'].map(selector => [selector, drawer?.querySelector(selector)?.scrollTop || 0]);
    // All fragments are validated before touching any visible part of the cart.
    for (const plan of prepared) {
      const target = document.querySelector(plan.target);
      if (!target) continue;
      target.innerHTML = plan.source.innerHTML;
      if (plan.target === 'cart-items' || plan.target === '#main-cart-footer') target.className = plan.source.className;
      if (plan.target === '#CartDrawer' && drawer) {
        drawer.classList.toggle('is-empty', !!plan.html.querySelector('cart-drawer.is-empty'));
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      }
    }
    // Notes typed during an in-flight response must not be replaced by an older saved note.
    if (noteDraft !== undefined) document.querySelectorAll('cart-note textarea').forEach(el => { el.value = noteDraft; });
    lock(); // Freshly rendered controls belong to the same pending operation.
    for (const [selector, top] of scroll) { const el = drawer?.querySelector(selector); if (el) el.scrollTop = top; }
    if (options.openDrawer && drawer) drawer.open(document.querySelector('#cart-icon-bubble'));
    if (wasOpen && drawer) {
      const field = [...drawer.querySelectorAll('[data-cart-line-key]')].find(el => el.dataset.cartLineKey === activeKey && el.getAttribute('name') === activeName);
      const focus = field || drawer.querySelector('.drawer__close');
      if (typeof trapFocus === 'function') trapFocus(drawer.querySelector('#CartDrawer'), focus);
    }
  }
  async function getSections(list) {
    const url = new URL(cartUrl(), location.origin);
    url.searchParams.set('sections', list.map(plan => plan.section).join(','));
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Cart refresh failed');
      return fragments(await response.json(), list);
    } finally { clearTimeout(timer); }
  }
  function publishState(source, result, variantId) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source, productVariantId: variantId, variantId, cartData: result, addaxCartSynced: true })).catch(console.error);
  }
  async function post(url, body) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    let response;
    let result;
    try {
      const isForm = body instanceof FormData;
      response = await fetch(url, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...(!isForm && { 'Content-Type': 'application/json' }) }, body: isForm ? body : JSON.stringify(body), signal: controller.signal });
      result = await response.json();
      if (!result || typeof result !== 'object' || Array.isArray(result)) throw new Error('Invalid cart response');
    } catch (_) {
      uncertain = true;
      const error = new Error('We could not confirm the update. Refresh your bag before trying again.');
      report(error.message, true);
      throw error; // Never retry a mutation: it may already have succeeded.
    } finally { clearTimeout(timer); }
    if (!response.ok || result.status || result.errors) {
      const error = new Error(messageFor(result));
      error.status = response.status;
      if (response.status >= 500) uncertain = true;
      else {
        try { render(await getSections(plans())); }
        catch (_) { uncertain = true; }
      }
      report(error.message, uncertain);
      throw error;
    }
    return result;
  }
  async function commit(result, list, options) {
    let prepared;
    try { prepared = fragments(result.sections, list); }
    catch (_) {
      try { prepared = await getSections(list); }
      catch (_) {
        uncertain = true;
        const error = new Error('Your bag changed, but the latest details could not be loaded.');
        report(error.message, true);
        throw error;
      }
    }
    render(prepared, options);
  }

  const api = {
    version: '1.0.0',
    get busy() { return pending > 0 || uncertain; },
    report,
    add(formData, options = {}) {
      const data = new FormData();
      for (const [key, value] of formData) data.append(key, value);
      return run(async () => {
        const list = plans();
        data.set('sections', list.map(plan => plan.section).join(','));
        data.set('sections_url', location.pathname);
        const result = await post(endpoint('add'), data);
        // Closing a quick-add modal never schedules an old response to render later.
        if (options.beforeOpen) await options.beforeOpen();
        await commit(result, list, options);
        publishState(options.source || 'product-form', result, data.get('id'));
        return result;
      });
    },
    change(key, quantity, options = {}) {
      if (!key || !Number.isInteger(quantity) || quantity < 0) return Promise.reject(new Error('Please refresh your bag before changing this item.'));
      return run(async () => {
        const list = plans();
        const result = await post(endpoint('change'), { id: key, quantity, sections: list.map(plan => plan.section), sections_url: location.pathname });
        await commit(result, list, options);
        publishState('cart-items', result, options.variantId);
        return result;
      });
    },
    refresh(options = {}) {
      if (refreshPromise && !options.openDrawer) return refreshPromise;
      const job = run(async ticket => {
        let prepared;
        try { prepared = await getSections(plans()); }
        catch (error) { uncertain = true; report('The latest bag details could not be loaded.', true); throw error; }
        // A newer user intent has reserved a mutation: discard this older read.
        if (ticket === revision) render(prepared, options);
      });
      refreshPromise = job;
      job.finally(() => { if (refreshPromise === job) refreshPromise = undefined; }).catch(() => {});
      return job;
    },
    saveNote(value) {
      noteDraft = value;
      noteChangedAt = Date.now();
      document.querySelectorAll('cart-note textarea').forEach(el => { if (el.value !== value) el.value = value; });
      if (notePromise) return notePromise;
      const job = run(async () => {
        let saved;
        do {
          while (Date.now() - noteChangedAt < 300) await new Promise(resolve => setTimeout(resolve, 300));
          saved = noteDraft;
          await post(endpoint('update'), { note: saved });
        } while (saved !== noteDraft);
        if (noteDraft === saved) noteDraft = undefined;
      }, false);
      notePromise = job;
      job.finally(() => { if (notePromise === job) notePromise = undefined; }).catch(() => {});
      return job;
    },
  };
  window.AddaxCart = api;
  document.addEventListener('click', event => {
    if (!api.busy) return;
    const target = event.target.closest?.('[name="checkout"], a[href*="/checkout"], cart-remove-button, cart-items quantity-input button, cart-drawer quantity-input button');
    if (target) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('submit', event => {
    if (!api.busy) return;
    const form = event.target;
    if (event.submitter?.name === 'checkout' || form.id === 'CartDrawer-Form' || form.id === 'cart') {
      event.preventDefault(); event.stopImmediatePropagation();
      report(uncertain ? 'Refresh your bag before continuing to checkout.' : 'Your bag is updating. Please try again in a moment.', uncertain);
    }
  }, true);
  // A back/forward cached document may contain a cart snapshot from before checkout.
  window.addEventListener('pageshow', event => { if (event.persisted && !uncertain) api.refresh().catch(error => report(error.message, true)); });
})();


class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', event => {
      event.preventDefault();
      const items = this.closest('cart-items, cart-drawer-items');
      items?.updateQuantity(this.dataset.index, 0, event);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    // Capture the line key and reserve the operation immediately; no untracked debounce.
    this.addEventListener('change', event => {
      if (event.target.matches('input[name="updates[]"]')) this.validateQuantity(event);
    });
  }
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      if (event.addaxCartSynced) return;
      return this.onCartUpdate();
    });
  }
  disconnectedCallback() { this.cartUpdateUnsubscriber?.(); }
  onCartUpdate() {
    return window.AddaxCart.refresh().catch(error => window.AddaxCart.report(error.message, true));
  }
  validateQuantity(event) {
    const input = event.target;
    const value = Number(input.value);
    const min = Number(input.dataset.min || input.min || 0);
    const max = input.max === '' ? Infinity : Number(input.max);
    const step = Number(input.step || 1);
    let message = '';
    if (input.value.trim() === '' || !Number.isInteger(value) || value < 0) message = 'Please enter a valid quantity.';
    else if (value !== 0 && value < min) message = window.quickOrderListStrings?.min_error?.replace('[min]', min) || `The minimum quantity is ${min}.`;
    else if (value > max) message = window.quickOrderListStrings?.max_error?.replace('[max]', max) || `The maximum quantity is ${max}.`;
    else if (value !== 0 && value % step !== 0) message = window.quickOrderListStrings?.step_error?.replace('[step]', step) || `Please use multiples of ${step}.`;
    // Reverting to the saved quantity must also release native checkout validation.
    input.setCustomValidity('');
    if (message) {
      input.value = input.getAttribute('value');
      window.AddaxCart.report(message);
      return;
    }
    this.updateQuantity(input.dataset.index, value, event, input.name, input.dataset.quantityVariantId);
  }
  onChange(event) { this.validateQuantity(event); }
  async updateQuantity(line, quantity, event, name, variantId) {
    const row = event?.target?.closest('.cart-item');
    const field = row?.querySelector('input[name="updates[]"]') || this.querySelector(`#Drawer-quantity-${line}, #Quantity-${line}`);
    const key = field?.dataset.cartLineKey || row?.querySelector('cart-remove-button')?.dataset.cartLineKey;
    const title = row?.querySelector('.cart-item__name')?.textContent.trim() || 'Item';
    try {
      const result = await window.AddaxCart.change(key, Number(quantity), { variantId });
      const returned = result.items?.find(item => item.key === key);
      let message = quantity === 0 ? `${title} removed from your bag.` : 'Your bag has been updated.';
      if (returned && returned.quantity !== Number(quantity)) message = `Only ${returned.quantity} of this item could be kept in your bag.`;
      document.querySelectorAll('#CartDrawer-LiveRegionText, #cart-live-region-text').forEach(el => { el.removeAttribute('aria-hidden'); el.textContent = message; });
    } catch (error) {
      if (field?.isConnected) field.value = field.getAttribute('value');
      window.AddaxCart.report(error.message, window.AddaxCart.busy);
    }
  }
  getSectionsToRender() {
    return [{ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
  }
}
customElements.define('cart-items', CartItems);
if (!customElements.get('cart-note')) customElements.define('cart-note', class CartNote extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('input', event => window.AddaxCart.saveNote(event.target.value).catch(error => window.AddaxCart.report(error.message, true)));
  }
});
